import React, { useEffect, useMemo, useState } from "react";
import { GetPaidCollectionsReport, GetUsersByDepartment } from "../API/RequestsServices";

// --- helpers ---
function formatBHD(n = 0) {
  try {
    const num = Number(n) || 0;
    return `BHD ${num.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}`;
  } catch {
    return `BHD ${n}`;
  }
}

const MONTH_COLORS = [
  { prog: "bg-emerald-500", bar: "from-emerald-400 to-emerald-600" },
  { prog: "bg-sky-500", bar: "from-sky-400 to-sky-600" },
  { prog: "bg-amber-500", bar: "from-amber-400 to-amber-600" },
  { prog: "bg-rose-500", bar: "from-rose-400 to-rose-600" },
  { prog: "bg-violet-500", bar: "from-violet-400 to-violet-600" },
  { prog: "bg-cyan-500", bar: "from-cyan-400 to-cyan-600" },
  { prog: "bg-fuchsia-500", bar: "from-fuchsia-400 to-fuchsia-600" },
  { prog: "bg-lime-500", bar: "from-lime-400 to-lime-600" },
  { prog: "bg-orange-500", bar: "from-orange-400 to-orange-600" },
  { prog: "bg-teal-500", bar: "from-teal-400 to-teal-600" },
  { prog: "bg-blue-500", bar: "from-blue-400 to-blue-600" },
  { prog: "bg-pink-500", bar: "from-pink-400 to-pink-600" },
];

// Safe reader for ownerOf (handles JSON or comma string)
function getOwnerOfFromSession() {
  const raw = sessionStorage.getItem("ownerOf");
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch {
    return raw.split(",").map((s) => s.trim()).filter(Boolean);
  }
}

// Canonical Digital Channel usernames
const DIGITAL_CHANNELS = [
  "online.stc",
  "online.stcpay",
  "web.solidarity",
  "alsalam.solidarity",
  "mobile.solidarity",
  "alsalamapp.solidarity",
  "online.solidarity",
  "whatsapp.solidarity",
];

export default function CollectionsByMonthBoard({ toast }) {
  const token = sessionStorage.getItem("token") || "";

  // Departments the user manages
  const ownerOf = useMemo(() => getOwnerOfFromSession(), []);
  const [department, setDepartment] = useState(() =>
    ownerOf.length === 1 ? ownerOf[0] : ""
  );

  // Filters
  const [channel, setChannel] = useState("");      // only for Digital Channels
  const [username, setUsername] = useState("");    // only for non–Digital departments

  // Users list for selected non–Digital department
  const [deptUsers, setDeptUsers] = useState([]);  // array of { userId, userName, fullName? }
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Report data
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [yearIndex, setYearIndex] = useState(0);
  const [apiError, setApiError] = useState(false);

  const isDigitalChannels =
    department && department.toLowerCase() === "digital channels";

  // Fetch users when a non–Digital department is chosen
  useEffect(() => {
    if (!department || isDigitalChannels) {
      setDeptUsers([]);
      setUsername("");
      return;
    }

    let ignore = false;
    (async () => {
      try {
        setLoadingUsers(true);
        const list = await GetUsersByDepartment(token, department, toast);
        if (!ignore) {
          // Expecting list of { userId, userName, fullName? }
          setDeptUsers(Array.isArray(list) ? list : []);
          setUsername(""); // default to "All in Department"
        }
      } catch {
        if (!ignore) {
          setDeptUsers([]);
          setUsername("");
        }
      } finally {
        if (!ignore) setLoadingUsers(false);
      }
    })();

    return () => { ignore = true; };
  }, [department, isDigitalChannels, token, toast]);

  // Clear the other filter depending on department type
  useEffect(() => {
    if (isDigitalChannels) {
      setUsername("");
    } else {
      setChannel("");
    }
  }, [isDigitalChannels]);

  const load = async () => {
    setLoading(true);
    setApiError(false);

    const opts = {};
    if (department) {
      // If Digital Channels, pass optional channel
      if (isDigitalChannels) {
        if (channel) opts.channel = channel;
        opts.department = department;
      } else {
        // Non–Digital department: pass department, and optional username
        opts.department = department;
        if (username) opts.username = username;
      }
    }
    const res = await GetPaidCollectionsReport(token, toast, undefined, opts);

    const valid = res && Array.isArray(res.years);
    if (valid) {
      const sortedYears = [...res.years].sort((a, b) => b.year - a.year);
      setReport({ ...res, years: sortedYears });
      setYearIndex(0);
    } else {
      setReport(null);
      setApiError(true);
    }

    setLoading(false);
  };

  // initial load
  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const years = useMemo(() => report?.years ?? [], [report]);

  useEffect(() => {
    if (years.length && yearIndex >= years.length) setYearIndex(0);
  }, [years, yearIndex]);

  const current = useMemo(() => {
    const y = years[yearIndex] || null;
    if (!y) return null;

    const {
      year,
      yearPaidTotalWithoutVAT,
      yearUnpaidTotalWithoutVAT,
      months,
      yearPaidCount,
      yearUnpaidCount,
    } = y;

    const best =
      (months || []).reduce(
        (acc, m, idx) =>
          (Number(m?.paidTotalWithoutVAT) || 0) > (acc.value || 0)
            ? { idx, value: Number(m.paidTotalWithoutVAT) || 0 }
            : acc,
        { idx: -1, value: 0 }
      ) || { idx: -1, value: 0 };

    return {
      year,
      yearPaidTotalWithoutVAT,
      yearUnpaidTotalWithoutVAT,
      months: months || [],
      yearPaidCount,
      yearUnpaidCount,
      bestIdx: best.idx,
    };
  }, [years, yearIndex]);

  // ---------- Error: hide report UI ----------
  if (apiError) {
    return (
      <div className="mt-6">
        <Filters
          {...{
            ownerOf, department, setDepartment,
            isDigitalChannels, channel, setChannel,
            deptUsers, username, setUsername, loadingUsers,
            loading, load
          }}
        />
        <div className="rounded-xl border border-dashed border-red-300 bg-white p-6 text-sm text-red-700">
          Couldn’t load the report. Please adjust filters and try again.
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6">
      <Filters
        {...{
          ownerOf, department, setDepartment,
          isDigitalChannels, channel, setChannel,
          deptUsers, username, setUsername, loadingUsers,
          loading, load,
          years, yearIndex, setYearIndex,
          current
        }}
      />

      {/* Body */}
      {loading ? (
        <div className="flex items-center justify-center p-10">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
        </div>
      ) : !current ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-500">
          No data available for this year.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4">
          {current.months.map((m, i) => {
            const paid = Number(m.paidTotalWithoutVAT || 0);
            const unpaid = Number(m.unpaidTotalWithoutVAT || 0);
            const denom = paid + unpaid;
            const paidPercent = denom > 0 ? (paid / denom) * 100 : 0;

            const theme = MONTH_COLORS[i % MONTH_COLORS.length];
            const isBest = i === current.bestIdx;

            return (
              <div
                key={`${m.month}-${i}`}
                className={[
                  "relative rounded-2xl border bg-white p-4 shadow-sm transition",
                  "border-gray-200 hover:shadow-md",
                  isBest ? "ring-2 ring-indigo-500/30" : "",
                ].join(" ")}
              >
                {/* BEST MONTH Ribbon */}
                {isBest && (
                  <div
                    className="absolute -left-2 -top-2 flex items-center gap-1 rounded-md bg-indigo-600 px-2 py-1 text-[10px] font-semibold text-white shadow-md"
                    title="Highest Paid Total (No VAT) this year"
                    aria-label="Best Month"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3.5 w-3.5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M10 2l2.39 4.84L18 7.27l-3.9 3.8.92 5.36L10 14.77 4.98 16.43l.92-5.36L2 7.27l5.61-.43L10 2z" />
                    </svg>
                    Best Month
                  </div>
                )}

                {/* Title Row */}
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-sm font-medium text-gray-800">
                    {m.monthName}
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-medium text-red-700">
                      Expired: {m.expiredCount || 0}
                    </span>

                    <div
                      className={[
                        "rounded-full px-2 py-0.5 text-[10px] font-medium",
                        isBest
                          ? "bg-indigo-50 text-indigo-700"
                          : "bg-gray-100 text-gray-600",
                      ].join(" ")}
                      title="Paid as a share of Paid + Unpaid (No VAT)"
                    >
                      {paidPercent.toFixed(0)}% Paid
                    </div>
                  </div>
                </div>

                {/* Amounts */}
                <div className="text-sm text-gray-700">
                  <span className="text-gray-500 text-xs">Unpaid (No VAT): </span>
                  <span className="font-semibold text-sky-600">
                    {formatBHD(unpaid)}
                  </span>
                </div>
                <div className="text-sm text-gray-700">
                  <span className="text-gray-500 text-xs">Paid (No VAT): </span>
                  <span className="font-semibold text-emerald-600">
                    {formatBHD(paid)}
                  </span>
                </div>

                {/* Progress bar */}
                <div className="mt-3 h-2 w-full rounded-full bg-gray-100">
                  <div
                    className="h-2 rounded-full bg-emerald-500"
                    style={{ width: `${Math.min(100, paidPercent)}%` }}
                  />
                </div>

                <div className="mt-2 text-xs text-gray-500">
                  {m.paidCount || 0} paid / {m.unpaidCount || 0} unpaid (No VAT)
                </div>

                {/* Mini vertical bar */}
                <div className="mt-4 flex items-end gap-2">
                  <div
                    className={["w-6 rounded-md bg-gradient-to-b", theme.bar].join(
                      " "
                    )}
                    style={{
                      height: `${Math.max(6, paidPercent)}%`,
                      minHeight: 12,
                      maxHeight: 160,
                    }}
                    title={`${m.monthName}: ${paidPercent.toFixed(
                      1
                    )}% paid of (paid + unpaid) — No VAT`}
                  />
                  <div className="text-[10px] text-gray-400">Paid %</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Filters header extracted for readability */
function Filters({
  ownerOf, department, setDepartment,
  isDigitalChannels, channel, setChannel,
  deptUsers, username, setUsername, loadingUsers,
  loading, load,
  years = [], yearIndex = 0, setYearIndex = () => {},
  current = null
}) {
  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="text-base font-semibold text-gray-900">
          Collections by Month (No VAT)
        </h3>

        {/* Year switcher */}
        {!!years.length && (
          <select
            value={yearIndex}
            onChange={(e) => setYearIndex(Number(e.target.value))}
            disabled={loading}
            className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {years.map((y, idx) => (
              <option key={y.year} value={idx}>
                {y.year}
              </option>
            ))}
          </select>
        )}

        {/* Department selector (if user owns departments) */}
        {ownerOf.length > 0 && (
          <div className="flex items-center gap-2">
            <select
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              disabled={loading}
              className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              title="Select a department you manage. Leave blank for your personal scope."
            >
              <option value="">(My personal data)</option>
              {ownerOf.map((dep) => (
                <option key={dep} value={dep}>
                  {dep}
                </option>
              ))}
            </select>

            {/* If Digital Channels → show channel picker */}
            {isDigitalChannels && (
              <select
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
                disabled={loading}
                className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                title="Pick a single digital channel (optional). Leave blank for ALL channels."
              >
                <option value="">All Channels</option>
                {DIGITAL_CHANNELS.map((chn) => (
                  <option key={chn} value={chn}>
                    {chn}
                  </option>
                ))}
              </select>
            )}

            {/* If NON–Digital department → show username picker */}
            {!isDigitalChannels && !!department && (
              <select
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={loading || loadingUsers}
                className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                title="Pick a single user (optional). Leave blank for ALL users in this department."
              >
                <option value="">All Users in Department</option>
                {deptUsers.map((u) => (
                  <option key={u.userId || u.UserId} value={u.userName || u.UserName}>
                    {u.userName || u.UserName}
                  </option>
                ))}
              </select>
            )}

            <button
              onClick={load}
              disabled={loading || loadingUsers}
              className="px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white shadow-sm hover:bg-gray-50 disabled:opacity-60 active:scale-[0.99]"
            >
              Apply
            </button>
          </div>
        )}

        {/* Summary pills */}
        {current && (
          <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-gray-700">
            <span className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              Paid Total (No VAT): {formatBHD(current.yearPaidTotalWithoutVAT)}
            </span>
            <span className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1">
              <span className="h-2 w-2 rounded-full bg-sky-500" />
              Unpaid Total (No VAT): {formatBHD(current.yearUnpaidTotalWithoutVAT)}
            </span>
          </div>
        )}
      </div>

      <button
        onClick={load}
        disabled={loading || loadingUsers}
        className="px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white shadow-sm hover:bg-gray-50 disabled:opacity-60 active:scale-[0.99]"
      >
        Refresh
      </button>
    </div>
  );
}
